// Safe DOM builder — never uses innerHTML
export function el(
	tag: string,
	attrs?: Record<string, string | boolean | EventListener>,
	children?: (Node | string)[]
): HTMLElement {
	const element = document.createElement(tag);

	if (attrs) {
		for (const [key, value] of Object.entries(attrs)) {
			if (key.startsWith('on') && typeof value === 'function') {
				element.addEventListener(key.slice(2), value as EventListener);
			} else if (typeof value === 'boolean') {
				if (value) element.setAttribute(key, '');
			} else {
				element.setAttribute(key, value as string);
			}
		}
	}

	if (children) {
		for (const child of children) {
			if (typeof child === 'string') {
				element.appendChild(document.createTextNode(child));
			} else {
				element.appendChild(child);
			}
		}
	}

	return element;
}

// Safe text node — automatic HTML escaping
export function text(str: string): Text {
	return document.createTextNode(str);
}

// Date formatting using Intl (locale-aware)
const dateFormatter = new Intl.DateTimeFormat(undefined, {
	weekday: 'short',
	month: 'short',
	day: 'numeric'
});

const timeFormatter = new Intl.DateTimeFormat(undefined, {
	hour: '2-digit',
	minute: '2-digit'
});

export function formatDate(iso: string): string {
	return dateFormatter.format(new Date(iso));
}

export function formatTime(iso: string): string {
	return timeFormatter.format(new Date(iso));
}

// Check if a time slot is "all day" (00:00 to 23:59)
export function isAllDay(startTime: string, endTime: string): boolean {
	const start = new Date(startTime);
	const end = new Date(endTime);
	return start.getHours() === 0 && start.getMinutes() === 0 && end.getHours() === 23 && end.getMinutes() === 59;
}

// Group time slots by date string
export function groupByDate<T extends { start_time: string }>(slots: T[]): Map<string, T[]> {
	const groups = new Map<string, T[]>();
	for (const slot of slots) {
		const dateKey = new Date(slot.start_time).toDateString();
		const group = groups.get(dateKey) || [];
		group.push(slot);
		groups.set(dateKey, group);
	}
	return groups;
}

// localStorage helpers with dte_embed_ prefix
const STORAGE_PREFIX = 'dte_embed_';

export function getStorage(token: string): { participant_token?: string; voted?: boolean } | null {
	try {
		const raw = localStorage.getItem(STORAGE_PREFIX + token);
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
}

export function setStorage(token: string, data: { participant_token: string; voted: boolean }): void {
	try {
		localStorage.setItem(STORAGE_PREFIX + token, JSON.stringify(data));
	} catch {
		// localStorage unavailable (private browsing, storage full, etc.)
	}
}

// API base URL — derived from the script's own src
export function getApiBase(): string {
	const scripts = document.querySelectorAll('script[src*="embed.js"]');
	for (const script of scripts) {
		const src = (script as HTMLScriptElement).src;
		try {
			const url = new URL(src);
			return url.origin;
		} catch {
			// continue
		}
	}
	return 'https://dtelection.com';
}

// Vote icon characters
export function voteIcon(response?: string): string {
	switch (response) {
		case 'yes':
			return '✓';
		case 'maybe':
			return '?';
		case 'no':
			return '✗';
		default:
			return '○';
	}
}

// Embed API types
export interface EmbedPoll {
	id: string;
	title: string;
	description: string | null;
	poll_type: 'when' | 'what';
	status: string;
	location_text: string | null;
	settings: PollSettings;
	time_slots: TimeSlot[] | null;
	options: PollOption[] | null;
	participants: Participant[];
	vote_count: number;
	created_at: string;
	image_url: string | null;
	external_link_url: string | null;
	show_branding: boolean;
}

export interface PollSettings {
	anonymous_voting?: boolean;
	allow_maybe?: boolean;
	allow_multiple_votes_per_slot?: boolean;
	require_email?: boolean;
	notify_on_vote?: boolean;
	single_selection?: boolean;
}

export interface TimeSlot {
	id: string;
	start_time: string;
	end_time: string;
}

export interface PollOption {
	id: string;
	label: string;
	position: number;
}

export interface Participant {
	id: string;
	name: string;
	votes: { slot_id: string; response: string }[];
}

export type VoteMap = Record<string, 'yes' | 'no' | 'maybe'>;
